import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SiteHeader from '../../components/SiteHeader/SiteHeader';
import styles from './InscriptionPage.module.css';
import {
  calculateDiscountedTuition,
  findApplicablePromotion,
} from '../../utils/promotionPricing';

const ZAPIER_WEBHOOK_PLACEHOLDER = "PEGA_AQUI_TU_WEBHOOK_DE_ZAPIER";
const ZAPIER_WEBHOOK_URL =
  process.env.REACT_APP_ZAPIER_WEBHOOK_URL || ZAPIER_WEBHOOK_PLACEHOLDER;
const OPENPAY_SANDBOX_ENABLED = process.env.REACT_APP_OPENPAY_SANDBOX === 'true';
const SANDBOX_VERIFICATION_BYPASS_EMAIL = String(
  process.env.REACT_APP_OPENPAY_SANDBOX_BYPASS_EMAIL || ''
).trim().toLowerCase();

function sanitizeValue(value, maxLength = 2000) {
  return String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .slice(0, maxLength);
}

async function sendToZapier(data) {
  if (
    !ZAPIER_WEBHOOK_URL ||
    ZAPIER_WEBHOOK_URL === ZAPIER_WEBHOOK_PLACEHOLDER
  ) {
    console.warn("Webhook de Zapier no configurado.");
    return;
  }

  const body = new URLSearchParams();

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      body.append(key, sanitizeValue(value));
    }
  });

  await fetch(ZAPIER_WEBHOOK_URL, {
    method: "POST",
    mode: "no-cors",
    body,
  });
}

const API_URL = process.env.REACT_APP_API_URL || '/api';

const OFERTA_CATEGORIES = [
  { label: 'Preparatoria', type: 'bachillerato', level: 'bachillerato', endpoint: `${API_URL}/bachillerato/list` },
  { label: 'Técnico Superior Universitario', type: 'tsu', level: 'tsu', endpoint: `${API_URL}/tsu/list` },
  { label: 'Licenciaturas', type: 'degree', level: 'licenciatura', endpoint: `${API_URL}/degrees/list` },
  { label: 'Maestrías', type: 'master', level: 'maestria', endpoint: `${API_URL}/master-degrees/list` },
  { label: 'Especializaciones', type: 'speciality', level: 'especialidad', endpoint: `${API_URL}/specialities/list` },
];

const EDUCATION_LEVEL_ALIASES = {
  bachillerato: 'bachillerato',
  preparatoria: 'bachillerato',
  tsu: 'tsu',
  degree: 'licenciatura',
  licenciatura: 'licenciatura',
  licenciaturas: 'licenciatura',
  master: 'maestria',
  maestria: 'maestria',
  maestrias: 'maestria',
  speciality: 'especialidad',
  specialty: 'especialidad',
  especialidad: 'especialidad',
  especialidades: 'especialidad',
};

const normalizeEducationLevel = (value) => EDUCATION_LEVEL_ALIASES[
  String(value || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
] || '';

const BECAS = [
  {
    id: 'preferente',
    label: 'BECA PREFERENTE',
    off: '65% OFF',
    discountPct: 65,
    bullets: [
      'Se cobra automáticamente con tarjeta de crédito o débito',
      'Sin preocuparte por pagos cada mes',
    ],
  },
  {
    id: 'flexible',
    label: 'BECA FLEXIBLE',
    off: '40% OFF',
    discountPct: 40,
    bullets: [
      'Pago manual con tarjeta de crédito o débito',
      'El pago no es automatizado',
    ],
  },
];

const parseTuition = (str) => {
  if (str == null || str === '') return null;
  if (typeof str === 'number') return Number.isFinite(str) ? str : null;

  const num = parseFloat(String(str).replace(/[^0-9.]/g, ''));
  return isNaN(num) ? null : num;
};

const formatMXN = (amount) =>
  amount.toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 2,
  });

const formatWeeklyTalkDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('es-MX', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'America/Mexico_City',
  }).format(date);
};

const formatWeeklyTalkTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'America/Mexico_City',
  }).format(date);
};

const shouldBypassEmailVerification = (email) => (
  OPENPAY_SANDBOX_ENABLED
  && Boolean(SANDBOX_VERIFICATION_BYPASS_EMAIL)
  && String(email || '').trim().toLowerCase() === SANDBOX_VERIFICATION_BYPASS_EMAIL
);

function InscriptionPage({
  initialEmail = '',
  initialUserDataId = '',
  initialCareerId = '',
  initialPhone = '',
  purpose = 'inscription',
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nombreParam = searchParams.get('nombre') || '';
  const nombreParts = nombreParam.trim().split(/\s+/).filter(Boolean);
  const initialNombre = searchParams.get('firstName') || nombreParts[0] || '';
  const initialApellidoPaterno = searchParams.get('apellidoPaterno') || searchParams.get('lastName') || (
    !searchParams.get('apellidoPaterno') && nombreParts.length > 1 ? nombreParts[1] : ''
  );
  const initialApellidoMaterno = searchParams.get('apellidoMaterno') || searchParams.get('motherLastName') || (
    !searchParams.get('apellidoMaterno') && nombreParts.length > 2 ? nombreParts.slice(2).join(' ') : ''
  );
  const paymentModeParam = searchParams.get('paymentMode');
  const preliminaryUserDataId = searchParams.get('userDataId') || initialUserDataId;
  const waLead = searchParams.get('waLead') || '';
  const initialPaymentMode = BECAS.some((b) => b.id === paymentModeParam) ? paymentModeParam : 'preferente';
  const [ofertaLists, setOfertaLists] = useState(
    Object.fromEntries(OFERTA_CATEGORIES.map((c) => [c.type, []]))
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const dynamicCategories = OFERTA_CATEGORIES.filter((c) => c.endpoint);
    Promise.all(dynamicCategories.map((c) => fetch(c.endpoint).then((r) => r.json())))
      .then((results) => {
        const updated = {};
        dynamicCategories.forEach((c, i) => {
          updated[c.type] = results[i].data || [];
        });
        setOfertaLists((prev) => ({ ...prev, ...updated }));

        const careerId = searchParams.get('careerId') || initialCareerId;
        if (careerId) {
          const allItems = Object.values(updated).flat();
          if (allItems.some((d) => d._id === careerId)) {
            setFormData((prev) => ({ ...prev, carrera: careerId }));
          }
        }
      })
      .catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const [formData, setFormData] = useState({
    carrera: '',
    nombre: initialNombre,
    apellidoMaterno: initialApellidoMaterno,
    apellidoPaterno: initialApellidoPaterno,
    fechaNacimiento: searchParams.get('fechaNacimiento') || searchParams.get('birthDate') || '',
    email: searchParams.get('email') || initialEmail,
    movil: searchParams.get('phone') || searchParams.get('movil') || initialPhone,
    aceptaAvisoPrivacidad: false,
  });
  const [selectedPlan, setSelectedPlan] = useState(initialPaymentMode);
  const [activePromotions, setActivePromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [weeklyTalkConfirmation, setWeeklyTalkConfirmation] = useState(null);
  const [weeklyTalkEvents, setWeeklyTalkEvents] = useState([]);
  const [selectedWeeklyTalkEventId, setSelectedWeeklyTalkEventId] = useState('');
  const [weeklyTalkFallback, setWeeklyTalkFallback] = useState(false);
  const [weeklyTalkLoading, setWeeklyTalkLoading] = useState(false);
  const [weeklyTalkAvailabilityError, setWeeklyTalkAvailabilityError] = useState('');
  const isWeeklyTalk = purpose === 'weekly-talk';

  const allOptions = Object.values(ofertaLists).flat();
  const selectedCareer = allOptions.find((d) => d._id === formData.carrera);
  const selectedCareerLevel = OFERTA_CATEGORIES.find((category) => (
    (ofertaLists[category.type] || []).some((item) => item._id === formData.carrera)
  ))?.level || '';
  const levelFromUrl = normalizeEducationLevel(
    searchParams.get('level')
      || searchParams.get('educationLevel')
      || searchParams.get('careerType')
      || searchParams.get('type')
  );
  const requestedEducationLevel = selectedCareerLevel || levelFromUrl;
  const weeklyTalkEvent = weeklyTalkEvents.find((event) => (
    String(event.eventId) === String(selectedWeeklyTalkEventId)
  )) || null;
  const tuition = parseTuition(selectedCareer?.costs?.tuition);
  const activePromotion = findApplicablePromotion(activePromotions, formData.carrera);

  useEffect(() => {
    fetch(`${API_URL}/promotions/active`)
      .then((response) => response.json())
      .then(({ data }) => setActivePromotions(Array.isArray(data) ? data : []))
      .catch(() => setActivePromotions([]));
  }, []);

  useEffect(() => {
    if (!isWeeklyTalk) return undefined;
    let active = true;
    setWeeklyTalkLoading(true);
    setWeeklyTalkAvailabilityError('');
    const query = requestedEducationLevel
      ? `?level=${encodeURIComponent(requestedEducationLevel)}`
      : '';
    fetch(`${API_URL}/events/available${query}`)
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'No hay una charla disponible.');
        if (active) {
          const availableEvents = Array.isArray(data.data) ? data.data : [];
          setWeeklyTalkEvents(availableEvents);
          setSelectedWeeklyTalkEventId((current) => (
            availableEvents.some((event) => String(event.eventId) === String(current))
              ? current
              : String(availableEvents[0]?.eventId || '')
          ));
          setWeeklyTalkFallback(Boolean(data.fallback));
        }
      })
      .catch((requestError) => {
        if (active) {
          setWeeklyTalkEvents([]);
          setSelectedWeeklyTalkEventId('');
          setWeeklyTalkFallback(false);
          setWeeklyTalkAvailabilityError(requestError.message || 'No se pudo consultar la próxima charla.');
        }
      })
      .finally(() => {
        if (active) setWeeklyTalkLoading(false);
      });
    return () => { active = false; };
  }, [isWeeklyTalk, requestedEducationLevel]);

  const canSubmit = (isWeeklyTalk || selectedPlan !== null)
    && formData.aceptaAvisoPrivacidad
    && (!isWeeklyTalk || Boolean(weeklyTalkEvent));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.carrera) {
      setError('Por favor selecciona una carrera.');
      return;
    }

    if (!isWeeklyTalk && !selectedPlan) {
      setError('Por favor selecciona un plan de inscripción.');
      return;
    }

    if (!formData.aceptaAvisoPrivacidad) {
      setError('Debes aceptar el aviso de privacidad.');
      return;
    }

    if (isWeeklyTalk && !weeklyTalkEvent) {
      setError('No hay una charla disponible para confirmar el registro.');
      return;
    }

    setLoading(true);
    try {
  const beca = isWeeklyTalk ? null : BECAS.find((b) => b.id === selectedPlan);

  const promotionDiscount = beca?.id === 'flexible'
    ? Number(activePromotion?.rawDisscount || 0)
    : 0;
  const planTotal = tuition != null
    ? calculateDiscountedTuition(tuition, beca?.discountPct || 0, promotionDiscount)
    : 0;

  const planLabel = [
    ...(beca ? [`${beca.label} (${beca.off})`] : []),
    ...(promotionDiscount > 0
      ? [`${activePromotion.discountLabel || `${promotionDiscount}% promoción`}`]
      : []),
  ].join(' + ');
  const bypassEmailVerification = shouldBypassEmailVerification(formData.email);
  const trackingParams =
    typeof window.getTrackingParams === "function"
      ? window.getTrackingParams()
      : {};

  const apiBody = {
    ...(!isWeeklyTalk && waLead ? { waLead } : {}),
    firstName: formData.nombre,
    lastName: formData.apellidoPaterno,
    motherLastName: formData.apellidoMaterno || undefined,
    birthDate: formData.fechaNacimiento,
    email: formData.email,
    phone: formData.movil,
    career: formData.carrera,
    careerLabel: selectedCareer?.name || '',
    ...(isWeeklyTalk ? {
      educationLevel: requestedEducationLevel,
      eventId: weeklyTalkEvent?.eventId,
    } : {}),
    utm_source: trackingParams.utm_source || '',
    utm_medium: trackingParams.utm_medium || '',
    utm_campaign: trackingParams.utm_campaign || '',
    utm_content: trackingParams.utm_content || '',
    utm_term: trackingParams.utm_term || '',
    ...(!isWeeklyTalk ? {
      paymentMode: beca.id,
      planLabel,
      planTotal,
      planDiscount: beca.discountPct,
    } : {}),
    ...(preliminaryUserDataId ? { userDataId: preliminaryUserDataId } : {}),
    sandboxEmailVerificationBypass: bypassEmailVerification,
    acceptsPrivacyNotice: formData.aceptaAvisoPrivacidad,
  };

  const response = await fetch(
    `${API_URL}/${isWeeklyTalk ? 'schedule-contacts' : 'inscription'}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(apiBody),
    }
  );

  const data = await response.json();

  if (!response.ok) {
    setError(data.message || "Ocurrió un error. Intenta de nuevo.");
    return;
  }

  if (isWeeklyTalk) {
    setWeeklyTalkConfirmation(data);
    window.dataLayer?.push({
      event: 'weekly_talk_registered',
      user_data_id: data.userDataId,
      schedule_contact_id: data.scheduleContactId,
      career_id: formData.carrera,
    });
    return;
  }

  try {
    await sendToZapier({
      firstName: formData.nombre,
      lastName: formData.apellidoPaterno,
      motherLastName: formData.apellidoMaterno || "",
      birthDate: formData.fechaNacimiento,
      email: formData.email,
      phone: formData.movil,
      career: formData.carrera,
      careerLabel: selectedCareer?.name || formData.carrera,
      paymentMode: beca.id,
      planLabel,
      planTotal,
      planDiscount: beca.discountPct,
      userDataId: data.userDataId,

      waLead,
      leadStage: 'registered',

      utm_source: trackingParams.utm_source || "",
      utm_medium: trackingParams.utm_medium || "",
      utm_campaign: trackingParams.utm_campaign || "",
      utm_content: trackingParams.utm_content || "",
      utm_term: trackingParams.utm_term || "",
      gclid: trackingParams.gclid || "",
      fbclid: trackingParams.fbclid || "",
      ad_id: trackingParams.ad_id || "",
      li_fat_id: trackingParams.li_fat_id || "",
      landing_page: trackingParams.landing_page || window.location.href,
      referrer: trackingParams.referrer || document.referrer || "",
      captured_at: trackingParams.captured_at || "",
      submitted_at: new Date().toISOString(),
    });
  } catch (webhookError) {
    console.warn("No se pudo enviar la información a Zapier:", webhookError);
  }

  if (bypassEmailVerification && data.verificationToken) {
    navigate(
      `/inscripcion/paso-3?id=${data.userDataId}&token=${data.verificationToken}&paymentMode=${encodeURIComponent(beca.id)}&careerLabel=${encodeURIComponent(selectedCareer?.name || formData.carrera)}&planLabel=${encodeURIComponent(planLabel)}&planTotal=${planTotal}&planDiscount=${beca.discountPct}${waLead ? `&waLead=${encodeURIComponent(waLead)}&leadStage=registered` : ''}`
    );
    return;
  }

  navigate("/inscripcion/paso-2", {
    state: {
      id: data.userDataId,
      waLead,
      leadStage: 'registered',
      careerLabel: selectedCareer?.name || formData.carrera,
      paymentMode: beca.id,
      planLabel,
      planTotal,
      planDiscount: beca.discountPct,
    },
  });
} catch {
  setError("No se pudo conectar con el servidor. Intenta de nuevo.");
} finally {
  setLoading(false);
}
  };

  if (weeklyTalkConfirmation) {
    return (
      <div className={styles.page}>
        <SiteHeader />
        <main className={styles.main}>
          <section className={styles.successCard} role="status">
            <span className={styles.successEyebrow}>Registro confirmado</span>
            <h1>Te esperamos en nuestra charla semanal</h1>
            <p>
              Guardamos tus datos y tu lugar para conocer nuestra oferta educativa
              y resolver tus dudas.
            </p>
            <div className={styles.successSchedule}>
              <strong>{weeklyTalkConfirmation.schedule?.title || 'Charla de admisiones'}</strong>
              <span>
                {formatWeeklyTalkDate(
                  weeklyTalkConfirmation.schedule?.scheduledAt
                ) || 'Próxima sesión'}
                {' · '}
                {formatWeeklyTalkTime(weeklyTalkConfirmation.schedule?.scheduledAt)} h
                {' · '}
                {weeklyTalkConfirmation.schedule?.durationMinutes || 60} minutos
              </span>
            </div>
            {weeklyTalkConfirmation.schedule?.meetUrl && (
              <a
                className={styles.meetLink}
                href={weeklyTalkConfirmation.schedule.meetUrl}
                target="_blank"
                rel="noreferrer"
              >
                Abrir enlace de Google Meet
              </a>
            )}
            <button
              type="button"
              className={styles.submitButton}
              onClick={() => navigate('/inscripcion')}
            >
              Continuar con mi inscripción
            </button>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <SiteHeader />

      <main className={styles.main}>
        <img
          className={styles.odmLogo}
          src="/logos/logos_2.png"
          alt="Logo ODM"
          onError={(e) => {
            e.target.src = '/logos/logos_1.png';
          }}
        />
        <div className={styles.titleBlock}>
          <h1 className={styles.pageTitle}>
            {isWeeklyTalk ? 'Charla semanal de admisiones' : 'Inscripciones'}
          </h1>
          <div className={styles.carreraRow}>
            <span className={styles.carreraLabel}>
              {isWeeklyTalk
                ? 'Selecciona la carrera que te interesa:'
                : 'Crea tu inscripción en la carrera de:'}
            </span>
            <div className={styles.selectWrapper}>
              <select
                name="carrera"
                className={styles.carreraSelect}
                value={formData.carrera}
                onChange={handleChange}
                aria-label="Selecciona una carrera"
              >
                <option value="" disabled>
                  Selecciona una carrera
                </option>
                {OFERTA_CATEGORIES.map((cat) => {
                  const items = ofertaLists[cat.type] || [];
                  if (items.length === 0) return null;
                  return (
                    <optgroup key={cat.type} label={cat.label}>
                      {items.map((d) => (
                        <option key={d._id} value={d._id}>
                          {d.name}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
              <span className={styles.selectChevron} aria-hidden="true">
                &#8964;
              </span>
            </div>
          </div>
        </div>

        {isWeeklyTalk && (
          <section className={styles.weeklyTalkEvent} aria-live="polite">
            {weeklyTalkLoading ? (
              <p>Consultando las próximas charlas…</p>
            ) : weeklyTalkEvents.length > 0 ? (
              <>
                <span className={styles.weeklyTalkEyebrow}>
                  {weeklyTalkEvents.length === 1 ? 'Próxima sesión' : 'Sesiones disponibles'}
                </span>
                {weeklyTalkFallback && requestedEducationLevel && (
                  <p className={styles.weeklyTalkFallback}>
                    No hay una charla específica para tu nivel en este momento.
                    Puedes elegir cualquiera de las sesiones disponibles.
                  </p>
                )}
                <div className={styles.weeklyTalkOptions}>
                  {weeklyTalkEvents.map((event) => {
                    const selected = String(event.eventId) === String(selectedWeeklyTalkEventId);
                    return (
                      <label
                        key={event.eventId}
                        className={`${styles.weeklyTalkOption} ${selected ? styles.weeklyTalkOptionSelected : ''}`}
                      >
                        <input
                          type="radio"
                          name="weeklyTalkEvent"
                          value={event.eventId}
                          checked={selected}
                          onChange={() => setSelectedWeeklyTalkEventId(String(event.eventId))}
                        />
                        <span>
                          <strong>{event.title}</strong>
                          <small>
                            {formatWeeklyTalkDate(event.scheduledAt)} ·{' '}
                            {formatWeeklyTalkTime(event.scheduledAt)} h ·{' '}
                            {event.durationMinutes} minutos
                          </small>
                          {event.description && <small>{event.description}</small>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className={styles.weeklyTalkUnavailable}>
                {weeklyTalkAvailabilityError || 'No hay charlas próximas disponibles.'}
              </p>
            )}
          </section>
        )}

        <form className={styles.formCard} onSubmit={handleSubmit} noValidate>
          <input
            type="text"
            name="nombre"
            className={styles.field}
            placeholder="Nombre"
            value={formData.nombre}
            onChange={handleChange}
            required
            autoComplete="given-name"
          />
          <input
            type="text"
            name="apellidoPaterno"
            className={styles.field}
            placeholder="Apellido Paterno"
            value={formData.apellidoPaterno}
            onChange={handleChange}
            required
          />
          <input
            type="text"
            name="apellidoMaterno"
            className={styles.field}
            placeholder="Apellido Materno (opcional)"
            value={formData.apellidoMaterno}
            onChange={handleChange}
            autoComplete="family-name"
          />
          <input
            type="date"
            name="fechaNacimiento"
            className={`${styles.field} ${!formData.fechaNacimiento ? styles.fieldPlaceholder : ''}`}
            value={formData.fechaNacimiento}
            onChange={handleChange}
            required
            autoComplete="bdate"
          />
          <input
            type="email"
            name="email"
            className={styles.field}
            placeholder="e.mail"
            value={formData.email}
            onChange={handleChange}
            required
            autoComplete="email"
          />
          <input
            type="tel"
            name="movil"
            className={styles.field}
            placeholder="Móvil"
            value={formData.movil}
            onChange={handleChange}
            required
            autoComplete="tel"
          />

          {/* ── Becas: sólo forman parte de la inscripción ── */}
          {!isWeeklyTalk && <div className={styles.plansSection}>
            <p className={styles.plansTitle}>Selecciona tu beca</p>
            <div className={styles.plansGrid}>
              {BECAS.map((beca) => {
                const isSelected = selectedPlan === beca.id;
                const promotionDiscount = beca.id === 'flexible'
                  ? Number(activePromotion?.rawDisscount || 0)
                  : 0;
                const discounted = tuition != null
                  ? calculateDiscountedTuition(
                    tuition,
                    beca.discountPct,
                    promotionDiscount
                  )
                  : null;
                return (
                  <button
                    key={`${beca.id}-${formData.carrera || 'sin-carrera'}`}
                    type="button"
                    className={`${styles.planCard} ${isSelected ? styles.planCardSelected : ''}`}
                    onClick={() => setSelectedPlan(beca.id)}
                    aria-pressed={isSelected}
                  >
                    <span className={styles.planLabel}>{beca.label}</span>
                    <span className={styles.planOff}>{beca.off}</span>
                    {promotionDiscount > 0 && (
                      <span className={styles.promotionApplied}>
                        + {activePromotion.discountLabel || `${promotionDiscount}% de promoción`}
                      </span>
                    )}

                    <div className={styles.planPriceBlock}>
                      {tuition != null && (
                        <span className={styles.planOriginalPrice}>
                          {formatMXN(tuition)}<span className={styles.planPriceSuffix}>/mes</span>
                        </span>
                      )}
                      {discounted != null ? (
                        <span className={styles.planDiscountedPrice}>
                          {formatMXN(discounted)}<span className={styles.planPriceSuffix}>/mes</span>
                        </span>
                      ) : (
                        <span className={styles.planNoPriceNote}>
                          Selecciona una carrera para ver el precio
                        </span>
                      )}
                    </div>

                    {isSelected && (
                      <ul className={styles.planBullets}>
                        {beca.bullets.map((b) => (
                          <li key={b} className={styles.planBulletItem}>{b}</li>
                        ))}
                      </ul>
                    )}
                  </button>
                );
              })}
            </div>
          </div>}

          <label className={styles.termsRow}>
            <input
              type="checkbox"
              name="aceptaAvisoPrivacidad"
              className={styles.checkbox}
              checked={formData.aceptaAvisoPrivacidad}
              onChange={handleChange}
            />
            <span className={styles.termsText}>
              He leído y acepto el{' '}
              <a
                href="/avisoPrivacidad.pdf"
                download
                className={styles.termsLink}
                onClick={(event) => event.stopPropagation()}
              >
                Aviso de Privacidad
              </a>
              .
            </span>
          </label>

          {error && <p className={styles.errorMsg}>{error}</p>}

          <div className={styles.submitRow}>
            <button
              type="submit"
              className={`${styles.submitButton} ${!canSubmit ? styles.submitButtonDisabledStyle : ''}`}
              disabled={loading || !canSubmit}
            >
              {loading
                ? 'Enviando...'
                : (isWeeklyTalk ? 'Inscribirme a la charla' : 'Siguiente paso')}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

export default InscriptionPage;
